import type { TypedSupabaseClient } from '../client';
import type {
  CreateDocumentPropertyDefinitionInput,
  DocumentPropertyDefinition,
  DocumentPropertyDocumentType,
  IDocumentPropertyRepository,
  UpdateDocumentPropertyDefinitionInput,
} from '@erp/data-access/interfaces';

function mapDefinition(row: Record<string, unknown>): DocumentPropertyDefinition {
  return {
    id: row['id'] as string,
    documentType: row['document_type'] as DocumentPropertyDocumentType,
    propertyKey: row['property_key'] as string,
    label: row['label'] as string,
    inputType: row['input_type'] as DocumentPropertyDefinition['inputType'],
    selectOptions: Array.isArray(row['select_options'])
      ? row['select_options'].filter((value): value is string => typeof value === 'string')
      : [],
    isRequired: row['is_required'] === true,
    showOnScreen: row['show_on_screen'] !== false,
    showOnPrint: row['show_on_print'] === true,
    sortOrder: Number(row['sort_order']) || 0,
    isActive: row['is_active'] !== false,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

export class SupabaseDocumentPropertyRepository implements IDocumentPropertyRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(documentType?: DocumentPropertyDocumentType): Promise<DocumentPropertyDefinition[]> {
    let query = (this.supabase as any)
      .from('document_property_definitions')
      .select('*')
      .eq('is_active', true);
    if (documentType) query = query.eq('document_type', documentType);
    const { data, error } = await query
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });
    if (error) throw error;
    return ((data ?? []) as Record<string, unknown>[]).map(mapDefinition);
  }

  async create(input: CreateDocumentPropertyDefinitionInput): Promise<DocumentPropertyDefinition> {
    const { data, error } = await (this.supabase as any)
      .from('document_property_definitions')
      .insert({
        document_type: input.documentType,
        property_key: input.propertyKey,
        label: input.label.trim(),
        input_type: input.inputType,
        select_options: input.selectOptions ?? [],
        is_required: input.isRequired ?? false,
        show_on_screen: input.showOnScreen ?? true,
        show_on_print: input.showOnPrint ?? false,
        sort_order: input.sortOrder ?? 0,
      })
      .select()
      .single();
    if (error) throw error;
    return mapDefinition(data as Record<string, unknown>);
  }

  async update(
    id: string,
    input: UpdateDocumentPropertyDefinitionInput,
  ): Promise<DocumentPropertyDefinition> {
    const patch: Record<string, unknown> = {};
    if (input.propertyKey !== undefined) patch['property_key'] = input.propertyKey;
    if (input.label !== undefined) patch['label'] = input.label.trim();
    if (input.inputType !== undefined) patch['input_type'] = input.inputType;
    if (input.selectOptions !== undefined) patch['select_options'] = input.selectOptions;
    if (input.isRequired !== undefined) patch['is_required'] = input.isRequired;
    if (input.showOnScreen !== undefined) patch['show_on_screen'] = input.showOnScreen;
    if (input.showOnPrint !== undefined) patch['show_on_print'] = input.showOnPrint;
    if (input.sortOrder !== undefined) patch['sort_order'] = input.sortOrder;
    if (input.isActive !== undefined) patch['is_active'] = input.isActive;
    const { data, error } = await (this.supabase as any)
      .from('document_property_definitions')
      .update(patch)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapDefinition(data as Record<string, unknown>);
  }

  async delete(id: string): Promise<void> {
    // Soft-delete: historical JSON values remain meaningful and recoverable.
    const { error } = await (this.supabase as any)
      .from('document_property_definitions')
      .update({ is_active: false })
      .eq('id', id);
    if (error) throw error;
  }
}
